import React from 'react';
import { Factory, Package, Briefcase, Settings, Clock, TrendingUp, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSearchSuggestions } from '@/hooks/useSearchSuggestions';
import { AutocompleteSuggestion } from '@/hooks/useSearchAutocomplete';
import { Button } from '@/components/ui/button';

interface SearchSuggestionsProps {
  onSuggestionClick: (term: string) => void;
  className?: string;
  recentSearches?: string[];
  onClearRecent?: () => void;
  autocompleteSuggestions?: AutocompleteSuggestion[];
  query?: string;
}

interface ChipGroupProps {
  label: string;
  icon: React.ReactNode;
  items: string[];
  onItemClick: (term: string) => void;
}

const ChipGroup: React.FC<ChipGroupProps> = ({ label, icon, items, onItemClick }) => {
  if (items.length === 0) return null;
  return (
    <div className="px-3 py-2">
      <div className="flex items-center gap-1.5 mb-1.5">
        {icon}
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item) => (
          <button
            key={item}
            onClick={() => onItemClick(item)}
            className={cn(
              "text-xs font-medium px-2.5 py-1 rounded-full",
              "bg-muted text-foreground",
              "hover:bg-primary/10 hover:text-primary",
              "active:scale-95 transition-all duration-150",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            )}
          >
            {item}
          </button>
        ))}
      </div>
    </div>
  );
};

const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  onSuggestionClick,
  className,
  recentSearches = [],
  onClearRecent,
  autocompleteSuggestions = [],
  query = '',
}) => {
  const { data, isLoading } = useSearchSuggestions();

  // Show autocomplete when user has typed 1+ chars
  const showAutocomplete = query.length >= 1 && autocompleteSuggestions.length > 0;

  // Recent searches section
  const hasRecent = recentSearches.length > 0;

  if (isLoading && !hasRecent && !showAutocomplete) {
    return (
      <div className={cn("p-4 text-center text-sm text-muted-foreground", className)}>
        Loading suggestions...
      </div>
    );
  }

  const hasStaticSuggestions = data && (
    data.manufacturers.length > 0 || data.partTypes.length > 0 ||
    data.jobCategories.length > 0 || data.serviceCategories.length > 0
  );

  if (!hasRecent && !showAutocomplete && !hasStaticSuggestions) return null;

  return (
    <div className={cn("py-1", className)}>
      {/* Recent Searches */}
      {hasRecent && !showAutocomplete && (
        <div className="px-3 py-2">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-muted-foreground" />
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Recent</span>
            </div>
            {onClearRecent && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearRecent}
                className="h-5 px-1.5 text-[10px] text-muted-foreground hover:text-foreground"
                aria-label="Clear recent searches"
              >
                <X className="w-3 h-3 mr-0.5" />
                Clear
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {recentSearches.slice(0, 5).map((term) => (
              <button
                key={term}
                onClick={() => onSuggestionClick(term)}
                className={cn(
                  "text-xs font-medium px-2.5 py-1 rounded-full",
                  "bg-muted text-foreground",
                  "hover:bg-primary/10 hover:text-primary",
                  "active:scale-95 transition-all duration-150",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                )}
              >
                {term}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Autocomplete Suggestions */}
      {showAutocomplete && (() => {
        const didYouMean = autocompleteSuggestions.filter(s => s.source === 'did_you_mean');
        const regular = autocompleteSuggestions.filter(s => s.source !== 'did_you_mean');
        const isDidYouMean = didYouMean.length > 0 && regular.length === 0;
        const displayList = isDidYouMean ? didYouMean : regular.length > 0 ? regular : autocompleteSuggestions;

        return (
          <div className="px-3 py-2">
            <div className="flex items-center gap-1.5 mb-1.5">
              {isDidYouMean ? (
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Did you mean...</span>
              ) : (
                <>
                  <TrendingUp className="w-3 h-3 text-muted-foreground" />
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Suggestions</span>
                </>
              )}
            </div>
            <div className="space-y-0.5">
              {displayList.map((s) => (
                <button
                  key={`${s.source}-${s.suggestion}`}
                  onClick={() => onSuggestionClick(s.suggestion)}
                  className={cn(
                    "w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md",
                    "text-sm text-foreground",
                    "hover:bg-muted/70 transition-colors duration-150",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isDidYouMean && "italic"
                  )}
                >
                  {s.source === 'trending' ? (
                    <TrendingUp className="w-3.5 h-3.5 text-primary/60 flex-shrink-0" />
                  ) : (
                    <span className="w-3.5 h-3.5 flex-shrink-0" />
                  )}
                  <span className="truncate">{s.suggestion}</span>
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Static category chips (only when no autocomplete active) */}
      {!showAutocomplete && data && (
        <>
          <div className="px-3 pt-1 pb-1.5">
            <span className="text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">Quick search</span>
          </div>
          <ChipGroup
            label="Manufacturers"
            icon={<Factory className="w-3 h-3 text-muted-foreground" />}
            items={data.manufacturers}
            onItemClick={onSuggestionClick}
          />
          <ChipGroup
            label="Part Types"
            icon={<Package className="w-3 h-3 text-muted-foreground" />}
            items={data.partTypes}
            onItemClick={onSuggestionClick}
          />
          <ChipGroup
            label="Job Categories"
            icon={<Briefcase className="w-3 h-3 text-muted-foreground" />}
            items={data.jobCategories}
            onItemClick={onSuggestionClick}
          />
          <ChipGroup
            label="Services"
            icon={<Settings className="w-3 h-3 text-muted-foreground" />}
            items={data.serviceCategories}
            onItemClick={onSuggestionClick}
          />
        </>
      )}
    </div>
  );
};

export default SearchSuggestions;
